import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';

import { getIFS } from '../../api';
import {
  type GetIFSInputInput,
  importExistingIFS,
  type ImportExistingIFSInput,
  importIFS,
  type ImportIFSInput,
} from '../../api/ifsSchema';
import { type TypeIFS } from '../../common/dataTypes';
import { type DefaultMutationError, type DefaultQueryError } from '../index';

export const useGetIFS = (
  input: GetIFSInputInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeIFS>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getIFS', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeIFS> = await getIFS(input);

      return data;
    },
    ...opts,
  });

export const useImportIFS = (
  opts?: Partial<
    UseMutationOptions<Awaited<any>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['importIFS'],
    mutationFn: async (input: ImportIFSInput) => {
      const data = await importIFS(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await queryClient.refetchQueries({
        queryKey: ['getIFS'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getDataTablesByDatasetId'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getPreDefinedExpenses'],
      });
      await queryClient.refetchQueries({
        queryKey: [
          'getProjectById',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
          },
        ],
      });
      await queryClient.refetchQueries({
        queryKey: ['getDatasetInputFiles'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getDatasets'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getModelOutput'],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useImportExistingIFS = (
  opts?: Partial<
    UseMutationOptions<Awaited<any>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['importExistingIFS'],
    mutationFn: async (input: ImportExistingIFSInput) => {
      const data = await importExistingIFS(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await queryClient.refetchQueries({
        queryKey: ['getIFS'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getDatasetInputFiles'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getDatasetInputFileById'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getModelOutput'],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};
